import { useCallback, useEffect, useRef } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { TRAFFICKING_TABLE_NAMES, TraffickingTableName } from '../../../../constants';
import type { UseFilters } from '../../../../contexts/FilterContext/hooks/useFilters';
import { getSelectedRowIdsFromState } from '../../utils';
import type { AdModel, CampaignModel, LineItemModel } from '../modelConverters';
import { getSelectionOrigin } from './cascadingUtils';
import type { SelectedRowIds } from './types';
import type { AssociatedEntities } from './useAssociatedEntities';
import type { TraffickerTableState } from './useTraffickerTable';

type CascadingTables = {
  campaigns: TraffickerTableState<CampaignModel>;
  lineItems: TraffickerTableState<LineItemModel>;
  ads: TraffickerTableState<AdModel>;
};

export type UseCascadingSelectionParams = {
  enabled: boolean;
  traffickerTables: CascadingTables;
  selectedRowIdsByTab: Record<TraffickingTableName, string[]>;
  applyTableFilter: UseFilters['applyTableFilter'];
  setIdsToQueryByTab: Record<TraffickingTableName, (ids: SelectedRowIds) => void>;
  getDescendantEntities: (tableName: TraffickingTableName, ids: string[]) => Promise<AssociatedEntities>;
  getAncestorEntities: (tableName: TraffickingTableName, ids: string[]) => AssociatedEntities;
};

const emptyAuthoredIds = (): Record<TraffickingTableName, Set<string>> => ({
  [TraffickingTableName.campaigns]: new Set<string>(),
  [TraffickingTableName.lineItems]: new Set<string>(),
  [TraffickingTableName.ads]: new Set<string>(),
});

/**
 * Cascades a row selection through the Campaign > LineItem > Ad hierarchy in both
 * directions (KATANA-1714).
 *
 * The cascade radiates from the row the user clicked to its ancestors and its
 * descendants, and never travels sideways: selecting one line item selects its parent
 * campaign and its own ads, but leaves that campaign's other line items alone.
 *
 * Ancestors come from the normalized Apollo cache and descendants from a server round trip.
 * Both legs are resolved before anything is written — writing one leg first would re-enter this
 * effect while the other is still in flight.
 */
const useCascadingSelection = ({
  enabled,
  traffickerTables,
  selectedRowIdsByTab,
  applyTableFilter,
  setIdsToQueryByTab,
  getDescendantEntities,
  getAncestorEntities,
}: UseCascadingSelectionParams): void => {
  // Ids this hook has already accounted for, per tab — both the ones it selected itself
  // and the origin ids of a cascade in flight. Subtracted from the observed diff so a
  // cascade's own writes never look like fresh user input on a later run. This — not a
  // boolean re-entrancy flag — is what keeps the cascade from reaching siblings: a
  // boolean cleared after the writes still races the FilterContext catching up, and in
  // that window the campaign we just selected upward reads as a new user selection and
  // fans back down over every line item it owns.
  const cascadeAuthoredIdsRef = useRef<Record<TraffickingTableName, Set<string>>>(emptyAuthoredIds());

  // A cascade outlives the effect run that started it. useTraffickerTable pushes the click
  // into the FilterContext in the same commit this effect runs in, so the effect is always
  // re-run — and previously torn down — while the descendant fetch is still in flight.
  // Only unmount may abandon the writes.
  const isMountedRef = useRef(true);

  useEffect(
    () => (): void => {
      isMountedRef.current = false;
    },
    []
  );

  const applySelections = useCallback(
    (tableName: TraffickingTableName, ids: string[]): void => {
      if (!ids.length) return;

      const { tableInstance } = traffickerTables[tableName];
      // Merged against live react-table state rather than the FilterContext copy, which
      // lags a render behind and would drop a concurrent cascade's writes.
      const existingSelectedRowIds = tableInstance.state.selectedRowIds;
      const idsToAdd = ids.filter((id) => !existingSelectedRowIds[id]);

      if (!idsToAdd.length) return;

      const merged = { ...existingSelectedRowIds, ...getSelectedRowIdsFromState(idsToAdd) };

      idsToAdd.forEach((id) => cascadeAuthoredIdsRef.current[tableName].add(id));

      tableInstance.state.selectedRowIds = merged;
      applyTableFilter(tableName, 'selectedRowIds', Object.keys(merged));
      setIdsToQueryByTab[tableName](merged);
    },
    [applyTableFilter, setIdsToQueryByTab, traffickerTables]
  );

  useEffect(() => {
    if (!enabled) return;

    const scopes = {
      [TraffickingTableName.campaigns]: {
        previous: selectedRowIdsByTab.campaigns,
        current: Object.keys(traffickerTables.campaigns.tableInstance.state.selectedRowIds),
      },
      [TraffickingTableName.lineItems]: {
        previous: selectedRowIdsByTab.lineItems,
        current: Object.keys(traffickerTables.lineItems.tableInstance.state.selectedRowIds),
      },
      [TraffickingTableName.ads]: {
        previous: selectedRowIdsByTab.ads,
        current: Object.keys(traffickerTables.ads.tableInstance.state.selectedRowIds),
      },
    };

    const origin = getSelectionOrigin(scopes);

    // Drop authored ids the FilterContext has caught up on. From then on they are
    // steady state, so a later re-selection of the same row is genuine user intent.
    TRAFFICKING_TABLE_NAMES.forEach((tableName) => {
      const authoredIds = cascadeAuthoredIdsRef.current[tableName];

      scopes[tableName].previous.forEach((id) => authoredIds.delete(id));
    });

    if (!origin) return;

    const userAddedIds = origin.addedIds.filter((id) => !cascadeAuthoredIdsRef.current[origin.tableName].has(id));

    if (!userAddedIds.length) return;

    // Claim the origin ids before awaiting. The effect re-runs while the fetch is in
    // flight, and until the FilterContext catches up the same ids still read as a fresh
    // diff — claiming them keeps one click to one cascade.
    userAddedIds.forEach((id) => cascadeAuthoredIdsRef.current[origin.tableName].add(id));

    const cascade = async (): Promise<void> => {
      const descendants = await getDescendantEntities(origin.tableName, userAddedIds);

      if (!isMountedRef.current) return;

      const ancestors = getAncestorEntities(origin.tableName, userAddedIds);

      // One commit for all four writes. React 17 does not batch after an await, and each write
      // changes an ads query variable, so unbatched they cost one GET_ADS refetch each.
      unstable_batchedUpdates(() => {
        applySelections(TraffickingTableName.campaigns, ancestors.campaigns ?? []);
        applySelections(TraffickingTableName.lineItems, ancestors.lineItems ?? []);
        applySelections(TraffickingTableName.lineItems, descendants.lineItems ?? []);
        applySelections(TraffickingTableName.ads, descendants.ads ?? []);
      });
    };

    // Every write is after the await, so a failed query applies nothing and the clicked row keeps
    // the selection react-table already gave it. Report and stop.
    cascade().catch((error) => {
      console.error('Cascading selection failed; selection left unchanged.', error);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    enabled,
    applySelections,
    getDescendantEntities,
    getAncestorEntities,
    selectedRowIdsByTab.campaigns,
    selectedRowIdsByTab.lineItems,
    selectedRowIdsByTab.ads,
    traffickerTables.campaigns.tableInstance.state.selectedRowIds,
    traffickerTables.lineItems.tableInstance.state.selectedRowIds,
    traffickerTables.ads.tableInstance.state.selectedRowIds,
  ]);
};

export default useCascadingSelection;
